import React from 'react';
import { Box, TextField, Typography } from '@material-ui/core';
import JsonViewer from './JsonViwer';
import { OutputViewToggle } from './ActionBtns';
import { makeStyles } from '@material-ui/core/styles';

export type ViewMode = 'encoded' | 'json';

interface Props {
    title?: string;
    encodedValue: string;
    jsonValue: any;
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
    onCopy?: (text: string) => void;
    maxHeight?: number;
}

const OutputPanel: React.FC<Props> = ({
    title = 'Output',
    encodedValue,
    jsonValue,
    viewMode,
    onViewModeChange,
    onCopy
}) => {
    const useStyles = makeStyles((theme) => ({
        outlinedBorder: {
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1eb89a',
                borderRadius: 8
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1eb89a',
                borderRadius: 8
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1eb89a'
            }
        },
        outputHeader: {
            gap: 8,
            [theme.breakpoints.down('xs')]: {
                flexWrap: 'wrap' as const
            }
        },
        outputHeaderTitle: {
            fontWeight: 600,
            fontSize: '0.95rem',
            color: '#1a1a2e',
            [theme.breakpoints.down('xs')]: {
                width: '100%'
            }
        }
    }));
    const classes = useStyles();

    return (
        <Box
            style={{
                border: '1px solid #e0e0e0',
                borderRadius: 12,
                overflow: 'hidden'
            }}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={3}
                py={2}
                className={classes.outputHeader}
                style={{ borderBottom: '1px solid #f0f0f0' }}>
                <Typography className={classes.outputHeaderTitle}>{title}</Typography>

                <OutputViewToggle
                    tab={viewMode === 'encoded' ? 0 : 1}
                    onTabChange={(t) => onViewModeChange(t === 0 ? 'encoded' : 'json')}
                    showCopy={!!encodedValue}
                    onCopy={() =>
                        onCopy?.(
                            viewMode === 'encoded'
                                ? encodedValue
                                : JSON.stringify(jsonValue, null, 2)
                        )
                    }
                />
            </Box>

            <Box p={2} m={2} style={{ maxHeight: 450, height: '100%', overflow: 'hidden' }}>
                {viewMode === 'encoded' && (
                    <TextField
                        value={encodedValue || ''}
                        fullWidth
                        multiline
                        variant="outlined"
                        minRows={10}
                        maxRows={15}
                        className={classes.outlinedBorder}
                        InputProps={{
                            readOnly: true,
                            style: {
                                fontFamily: 'monospace',
                                fontSize: '13px',
                                backgroundColor: '#f6f8fa',
                                padding: '18.5px 14px',
                                paddingRight: '0px'
                            }
                        }}
                        inputProps={{
                            style: {
                                wordBreak: 'break-all',
                                overflowWrap: 'anywhere'
                            }
                        }}
                    />
                )}

                {viewMode === 'json' && jsonValue && (
                    <JsonViewer content={jsonValue} maxHeight={237} />
                )}
            </Box>
        </Box>
    );
};

export default OutputPanel;
